import * as React from 'react';
import { hitApi } from '__common/utils/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  projectId: string;
  senderEmail?: string;
  renderTrigger: (open: () => void) => React.ReactNode;
  packetItems?: string[];
};

type State = {
  modalOpen: boolean;
  modalView: 'default' | 'success';
  recipientEmail: string;
  ccEmails: string;
  customMessage: string;
  sending: boolean;
  errorMessage: string;
};

const splitEmails = (emails: string) => emails
  .split(',')
  .map(email => email.trim())
  .filter(Boolean);

export default class SendBomToDistributor extends React.Component<Props, State> {
  private taskPollTimer: number | null = null;

  private taskTimeoutTimer: number | null = null;

  state: State = {
    modalOpen: false,
    modalView: 'default',
    recipientEmail: '',
    ccEmails: '',
    customMessage: '',
    sending: false,
    errorMessage: '',
  };

  componentWillUnmount() {
    this.clearTaskTimers();
  }

  openModal = () => {
    this.setState({
      modalOpen: true,
      modalView: 'default',
      recipientEmail: '',
      ccEmails: this.props.senderEmail || '',
      customMessage: '',
      sending: false,
      errorMessage: '',
    });
  }

  closeModal = () => {
    this.clearTaskTimers();
    this.setState({ modalOpen: false });
  }

  clearTaskTimers = () => {
    if (this.taskPollTimer) {
      window.clearTimeout(this.taskPollTimer);
      this.taskPollTimer = null;
    }
    if (this.taskTimeoutTimer) {
      window.clearTimeout(this.taskTimeoutTimer);
      this.taskTimeoutTimer = null;
    }
  }

  ccEmailsAreValid = () => splitEmails(this.state.ccEmails).every(email => EMAIL_RE.test(email));

  canSend = () => EMAIL_RE.test(this.state.recipientEmail.trim()) && this.ccEmailsAreValid() && !this.state.sending;

  failSend = (message: string) => {
    this.clearTaskTimers();
    this.setState({ sending: false, errorMessage: message });
  }

  taskFailureMessage = (result: any) => {
    if (typeof result === 'string' && result) {
      return `Email failed to send: ${result}`;
    }
    if (result && typeof result === 'object') {
      return `Email failed to send: ${JSON.stringify(result)}`;
    }
    return 'Email failed to send. Please verify the recipient and try again.';
  }

  sendPacket = () => {
    const { projectId } = this.props;
    const { recipientEmail, ccEmails, customMessage } = this.state;
    this.clearTaskTimers();
    this.setState({ sending: true, errorMessage: '' });

    const req = hitApi('POST', 'api/v1/ub_lite/bom/send_bom_email/', {
      project_id: projectId,
      distributor_email: recipientEmail.trim(),
      cc_emails: splitEmails(ccEmails).join(', '),
      email_body: customMessage,
    });

    fetch(req.url, {
      method: req.method,
      headers: req.headers as any,
      body: JSON.stringify(req.body),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        return res.json();
      })
      .then((data) => this.waitForEmailTask(data.task_id))
      .catch(() => {
        this.failSend('Failed to send email. Please try again.');
      });
  }

  waitForEmailTask = (taskId: string) => {
    if (!taskId) {
      this.failSend('Email task was not created. Please try again.');
      return;
    }

    this.taskTimeoutTimer = window.setTimeout(() => {
      this.failSend('Email is taking longer than expected. Please check again in a moment.');
    }, 120000);

    this.pollEmailTask(taskId);
  }

  pollEmailTask = (taskId: string) => {
    const req = hitApi('GET', `api/v1/task_info/${taskId}/`);
    fetch(req.url, {
      method: req.method,
      headers: req.headers as any,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data.status === 'SUCCESS') {
          this.clearTaskTimers();
          if (data.result && data.result.email_sent) {
            this.setState({ modalView: 'success', sending: false });
          } else {
            this.failSend('Email task finished without sending the package. Please try again.');
          }
          return;
        }
        if (data.status === 'FAILURE') {
          this.failSend(this.taskFailureMessage(data.result));
          return;
        }
        this.taskPollTimer = window.setTimeout(() => this.pollEmailTask(taskId), 2000);
      })
      .catch(() => {
        this.failSend('Unable to confirm email delivery. Please try again.');
      });
  }

  renderModal() {
    const {
      modalOpen,
      modalView,
      recipientEmail,
      ccEmails,
      customMessage,
      sending,
      errorMessage,
    } = this.state;
    if (!modalOpen) return null;

    const packetItems = this.props.packetItems || ['Bill of Materials'];
    const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) this.closeModal();
    };

    return (
      <div className="email-modal-overlay show" onClick={onBackdropClick}>
        <div className="email-modal-card">
          {modalView === 'default' ? (
            <>
              <div className="email-modal-hdr">
                <div>
                  <div className="email-modal-title">Send BOM</div>
                  <div className="email-modal-sub">We'll email the bill of materials report.</div>
                </div>
                <button type="button" className="email-modal-close" onClick={this.closeModal} aria-label="Close">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
              <div className="email-modal-body">
                <div className="email-modal-what">
                  {packetItems.map(item => (
                    <div className="email-modal-what-row" key={item}>
                      <div className="email-modal-what-dot" style={{ background: '#003B5C' }} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="email-modal-field">
                  <label htmlFor="bom-email-recipient">Recipient Email</label>
                  <input
                    id="bom-email-recipient"
                    className="email-modal-input"
                    type="email"
                    placeholder="e.g. client@example.com"
                    value={recipientEmail}
                    onChange={(e) => this.setState({ recipientEmail: e.target.value, errorMessage: '' })}
                  />
                </div>
                <div className="email-modal-field" style={{ marginTop: 12 }}>
                  <label htmlFor="bom-email-cc">
                    CC <span style={{ fontWeight: 400, color: '#5A7D95' }}>(optional)</span>
                  </label>
                  <input
                    id="bom-email-cc"
                    className="email-modal-input"
                    type="text"
                    placeholder="name@example.com, another@example.com"
                    value={ccEmails}
                    onChange={(e) => this.setState({ ccEmails: e.target.value, errorMessage: '' })}
                  />
                </div>
                <div className="email-modal-field" style={{ marginTop: 12 }}>
                  <label htmlFor="bom-email-message">
                    Message <span style={{ fontWeight: 400, color: '#5A7D95' }}>(optional)</span>
                  </label>
                  <textarea
                    id="bom-email-message"
                    className="email-modal-input"
                    rows={3}
                    placeholder="Add a note to the recipient..."
                    style={{ resize: 'vertical', lineHeight: 1.5 }}
                    value={customMessage}
                    onChange={(e) => this.setState({ customMessage: e.target.value, errorMessage: '' })}
                  />
                </div>
                {!this.ccEmailsAreValid() && (
                  <div className="email-modal-error">Enter valid CC addresses separated by commas.</div>
                )}
                {errorMessage && <div className="email-modal-error">{errorMessage}</div>}
              </div>
              <div className="email-modal-ftr">
                <button type="button" className="email-modal-btn email-modal-btn--ghost" onClick={this.closeModal}>Cancel</button>
                <button
                  type="button"
                  className="email-modal-btn email-modal-btn--primary"
                  onClick={this.sendPacket}
                  disabled={!this.canSend()}>
                  {sending ? 'Sending...' : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M1 6.5L12 1l-2.5 11-3.5-4L12 1M6 8.5L4.5 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Send BOM
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="email-success show">
              <div className="email-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12l5 5L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="email-success-title">Email Sent!</div>
              <div className="email-success-sub">BOM report sent to {recipientEmail.trim()}</div>
              <button type="button" className="email-modal-btn email-modal-btn--ghost" style={{ marginTop: 4 }} onClick={this.closeModal}>Done</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <>
        {this.props.renderTrigger(this.openModal)}
        {this.renderModal()}
      </>
    );
  }
}
